import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useSystemStatus } from '@/hooks/useSystemStatus';
import { tokenStorage } from '@/utils/token-storage';

const MAINTENANCE_EVENT = 'larkpm:maintenance';

interface MaintenancePayload {
  code?: string;
  message?: string;
}

function isPlatformAdminUser(): boolean {
  try {
    const user = tokenStorage.getUserData();
    return user?.isPlatformAdmin === true;
  } catch {
    return false;
  }
}

export function MaintenanceOverlay() {
  const { status } = useSystemStatus();
  const [forced, setForced] = useState<MaintenancePayload | null>(null);

  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<MaintenancePayload>).detail;
      if (detail?.code === 'MAINTENANCE') {
        setForced(detail);
      }
    };
    window.addEventListener(MAINTENANCE_EVENT, handler);
    return () => window.removeEventListener(MAINTENANCE_EVENT, handler);
  }, []);

  const fromPoll = status?.maintenanceMode && !isPlatformAdminUser();
  const active = fromPoll || !!forced;

  if (!active) return null;

  const message =
    forced?.message ||
    status?.deployNotice?.message ||
    'LarkPM is undergoing maintenance. Please try again shortly.';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 p-6">
      <div className="max-w-md text-center space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15">
          <AlertTriangle className="h-7 w-7 text-amber-600" />
        </div>
        <h1 className="font-newsreader text-2xl font-semibold">Under maintenance</h1>
        <p className="text-muted-foreground">{message}</p>
        <p className="text-xs text-muted-foreground">
          {status?.platformName ?? 'LarkPM'} will be back online soon.
        </p>
      </div>
    </div>
  );
}

export function dispatchMaintenanceMode(payload: MaintenancePayload) {
  window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT, { detail: payload }));
}
